// @flow
/* -----------------Globals--------------- */
import React, { useState } from 'react';

/* -----------------Third parties--------------- */
import { Controlled as ControlledZoom } from 'react-medium-image-zoom';
import { useLongPress } from 'use-long-press';
import clsx from 'clsx';

/* -----------------UI--------------- */
import {
  withStyles,
  useTheme,
} from '@material-ui/core/styles';

/* -----------------Types--------------- */
import type {
  Image,
} from 'common/types';

/* -----------------Styles--------------- */
const styles = theme => ({
  image: {
    backgroundRepeat: 'no-repeat',
    background: '#fff',
    backgroundSize: 'cover',
    height: 200,
    maxWidth: 300,
    borderRadius: 4,
    objectFit: 'contain',
    maxHeight: '100%',
    boxShadow: theme.shadows[1],
  },
  thumbnailContainer: {
    width: 'fit-content',
  },
});

/* -----------------Type declarations--------------- */
type Classes = {
  image: string,
  thumbnailContainer: string
};

/* -----------------Props--------------- */
type Props = {
  classes: Classes;
  image: Image,
  thumbnailStyle: any,
  customStyle: any,
  setZoomed: any,
  maxHeight: any,
  longPress?: boolean,
  isZoomed: boolean,
  handleSelect?: () => void
};

/* -----------------Component--------------- */
const ZoomImage = (props: Props) => {
  const {
    thumbnailStyle,
    customStyle,
    setZoomed,
    image: { url, name } = {},
    classes,
    isZoomed,
    longPress = false,
    handleSelect,
  } = props;

  const theme = useTheme();

  const [zoom, setZoom] = useState(false);

  const handleSetZoomed = (shouldZoom) => {
    if (typeof setZoomed === 'function') {
      if (longPress && handleSelect) {
        handleSelect();
      } else setZoomed(shouldZoom);
    } else {
      setZoom(shouldZoom);
    }
  };
  const bind = useLongPress((e) => {
    e.stopPropagation();
    if (longPress) {
      setZoomed(!isZoomed);
    }
  }, {
    onCancel: () => {
      if (isZoomed && longPress) {
        setZoomed(false);
      }
    },
    captureEvent: true,
    detect: 'both',
  });

  // TODO: Display close icon in zoomed view
  if (longPress && !isZoomed) {
    return (
      <div {...bind}>
        <img
          alt={name}
          src={url}
          className={clsx(classes.image, thumbnailStyle && thumbnailStyle)}
          style={{
            cursor: longPress && 'default',
            ...customStyle,
          }}
        />
      </div>
    );
  }
  return url ? (
    <div
      className={classes.thumbnailContainer}
      onClick={(e) => {
        e.stopPropagation();
        if ((longPress && zoom) || isZoomed) {
          setZoom(false);
          setZoomed(false);
        }
      }}
    >
      <ControlledZoom
        tabIndex={-1}
        isZoomed={zoom || isZoomed}
        onZoomChange={handleSetZoomed}
        overlayBgColorEnd="rgba(0,0,0, 0.8)"
        wrapStyle={{
          width: '100%',
          justifyContent: 'center',
        }}
      >
        <img
          alt={name}
          src={url}
          className={clsx(
            classes.image,
            thumbnailStyle && !zoom && thumbnailStyle,
          )}
          style={{
            cursor: longPress && 'default',
            borderRadius: zoom || isZoomed ? 0 : theme.shape.borderRadius,
            // TODO: This doesn't seem to work consistently
            ...!isZoomed && customStyle,
          }}
        />
      </ControlledZoom>
    </div>
  ) : null;
};

export default withStyles(styles)(ZoomImage);
